import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  Space,
  Typography,
} from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { PROJECT_DOCUMENTS_UPLOAD_MAX_COUNT } from "@/entities/project";
import { useOnFormClose, useGenericFormErrorNotification } from "@/hooks/forms";
import { useChain } from "@/hooks/useChain";
import DocumentsUploadField, {
  getMultipleDocumentsUploadFieldValue,
} from "@/modules/documents/documents-upload-field";
import { useFormValidation } from "@/modules/form-validation";
import LocationSelectParent from "@/modules/location/location-select-parent";
import { useOrganizationContextId } from "@/modules/organization/organization-context";
import { getSelectSingleValue } from "@/modules/select";
import { SidePanelFooter } from "@/modules/side-panel";
import { TranslateCostSymbol } from "@/modules/translate";
import { InputNumber } from "@/ui/input-number";
import { toISOString } from "@/utilities/date";

const { useForm } = Form;
const { Title } = Typography;

const ProjectForm = ({
  model,
  mutation,
  saveButtonLabel,
  initialValues,
  onMutate,
}) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const { contextId } = useOrganizationContextId();

  const onClose = useOnFormClose(form);
  const onMutateSuccess = useChain([onClose, onMutate]);

  useGenericFormErrorNotification(mutation.isError);

  const { validationErrors, validation } = useFormValidation(
    mutation.isError,
    mutation.error,
    [
      "name",
      "description",
      "reference",
      "start_date",
      "due_date",
      "revised_interventions_cost",
      "state",
      "location_id",
      "documents",
    ]
  );

  const onSave = (formValues) => {
    const data = {
      name: form.getFieldValue("name"),
      reference: form.getFieldValue("reference"),
      description: form.getFieldValue("description"),
      start_date: toISOString(form.getFieldValue("start_date")),
      due_date: toISOString(form.getFieldValue("due_date")),
      revised_interventions_cost:
        form.getFieldValue("revised_interventions_cost") || null, // zero means no project cost from the API perspective
      organization_id: contextId,
      location_id: getSelectSingleValue(form, "location") || null,
      documents: getMultipleDocumentsUploadFieldValue({
        formValue: formValues.documents,
        defaultDocuments: model?.documents,
      }),
    };

    mutation.mutate(data, { onSuccess: onMutateSuccess });
  };

  return (
    <Form
      id="projectForm"
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFinish={onSave}
      role="form"
    >
      <Title level={3}>{t("project:form.associateWith")}</Title>
      <LocationSelectParent {...validation("location_id")} />
      <Divider />
      <Title level={3}>{t("project:form.informations")}</Title>
      <Form.Item
        name="name"
        label={t("project:form.name.label")}
        rules={[{ required: true }]}
        {...validation("name")}
      >
        <Input placeholder={t("project:form.name.label")} data-testid="name" />
      </Form.Item>
      <Form.Item
        name="reference"
        label={t("project:form.reference.label")}
        {...validation("reference")}
      >
        <Input
          placeholder={t("project:form.reference.label")}
          data-testid="reference"
        />
      </Form.Item>
      <Form.Item
        name="description"
        label={t("project:form.description.label")}
        {...validation("description")}
      >
        <Input
          placeholder={t("project:form.description.label")}
          data-testid="description"
        />
      </Form.Item>
      <Form.Item
        name="start_date"
        label={t("project:form.startDate.label")}
        rules={[{ required: true }]}
        {...validation("start_date")}
      >
        <DatePicker data-testid="start_date" />
      </Form.Item>
      <Form.Item
        name="due_date"
        label={t("project:form.dueDate.label")}
        rules={[{ required: true }]}
        {...validation("due_date")}
      >
        <DatePicker data-testid="due_date" />
      </Form.Item>
      <Form.Item
        name="revised_interventions_cost"
        label={t("project:form.revisedInterventionsCost.label")}
        {...validation("revised_interventions_cost")}
      >
        <InputNumber
          min={0}
          data-testid="revised_interventions_cost"
          addonAfter={<TranslateCostSymbol />}
        />
      </Form.Item>
      <DocumentsUploadField
        name="documents"
        label={t("project:form.documents.label")}
        documents={model?.documents}
        uploadProps={{ maxCount: PROJECT_DOCUMENTS_UPLOAD_MAX_COUNT }}
        {...validation("documents")}
      />
      {validationErrors().others && <Form.Item {...validation("others")} />}

      <SidePanelFooter>
        <Space>
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            htmlType="submit"
            form="projectForm"
          >
            {saveButtonLabel}
          </Button>
        </Space>
      </SidePanelFooter>
    </Form>
  );
};

const initialValuesShape = PropTypes.shape({
  name: PropTypes.string,
  reference: PropTypes.string,
  description: PropTypes.string,
  start_date: PropTypes.object,
  due_date: PropTypes.object,
  total_cost: PropTypes.number,
  location: PropTypes.object,
  documents: PropTypes.array,
});

ProjectForm.propTypes = {
  model: PropTypes.object,
  mutation: PropTypes.object.isRequired,
  saveButtonLabel: PropTypes.string.isRequired,
  initialValues: initialValuesShape.isRequired,
  onMutate: PropTypes.func,
};

export default ProjectForm;
